<template>
  <section
    class="message-list border rounded bg-body-emphasis p-0"
    aria-label="Messages"
  >
    <header
      class="d-flex justify-content-between align-items-center gap-2 border-bottom px-3 py-2 small"
    >
      <strong aria-live="polite">{{ heading }}</strong>
      <span class="text-muted">Times in UTC</span>
    </header>
    <p v-if="status === 'loading'" class="p-4 mb-0 text-center" role="status">
      Loading messages…
    </p>
    <div v-else-if="status === 'error'" class="p-4 text-center" role="alert">
      Could not load messages.
      <button class="btn btn-link" @click="$emit('retry')">Try again</button>
    </div>
    <div
      v-else-if="status === 'ready' && !messages.length"
      class="p-5 text-center text-muted"
    >
      <p class="mb-1 fw-semibold">No messages match.</p>
      <p class="mb-0 small">
        Try fewer words, a wider date range, or another conversation.
      </p>
    </div>
    <template v-for="group in groups" :key="group.day">
      <h6 class="day-label px-3 py-1 mb-0 text-muted">
        {{ formatDayLabel(group.day) }}
      </h6>
      <button
        v-for="message in group.messages"
        :key="message.id"
        class="message-row"
        :class="{ selected: message.id === selectedId }"
        :aria-current="message.id === selectedId ? 'true' : undefined"
        @click="$emit('select', message.id)"
      >
        <time class="clock text-muted small" :datetime="message.timestamp">{{
          formatClock(message.timestamp)
        }}</time>
        <div class="min-w-0">
          <div class="d-flex flex-wrap align-items-baseline gap-2 small">
            <strong class="text-body">{{
              message.sender ?? "Unknown sender"
            }}</strong>
            <span
              v-if="showConversation && message.conversation_name"
              class="text-muted text-truncate"
            >
              in {{ message.conversation_name }}
            </span>
            <span
              v-if="message.trade_signal_count"
              class="badge bg-success-subtle text-success-emphasis ms-auto"
              >{{ message.trade_signal_count }}
              {{
                message.trade_signal_count === 1 ? "signal" : "signals"
              }}</span
            >
          </div>
          <TradeChatHighlightComponent
            class="body"
            :text="message.body ?? ''"
            :terms
          />
        </div>
      </button>
    </template>
    <p v-if="moreFailed" class="small text-danger px-3 my-2" role="alert">
      Could not load more messages. Try again.
    </p>
    <button
      v-if="hasMore"
      class="btn btn-link w-100 border-top"
      :disabled="loadingMore"
      @click="$emit('more')"
    >
      {{
        loadingMore
          ? "Loading…"
          : `Load more (${(total - messages.length).toLocaleString()} left)`
      }}
    </button>
  </section>
</template>
<script setup lang="ts">
import { computed } from "vue";
import TradeChatHighlightComponent from "./TradeChatHighlightComponent.vue";
import {
  formatClock,
  formatDayLabel,
  groupByDay,
} from "./tradeChatLogsPresenters";
import type { ChatMessage } from "./tradeChatLogsTypes";

const props = defineProps<{
  messages: ChatMessage[];
  total: number;
  status: "idle" | "loading" | "ready" | "error";
  hasMore: boolean;
  loadingMore: boolean;
  moreFailed: boolean;
  terms: string[];
  selectedId: number | null;
  /** Off inside a single conversation, where repeating its name on every row is noise. */
  showConversation: boolean;
}>();
defineEmits<{
  select: [id: number];
  more: [];
  retry: [];
}>();

const groups = computed(() => groupByDay(props.messages));
const heading = computed(() =>
  props.status === "ready"
    ? `${props.total.toLocaleString()} ${props.total === 1 ? "message" : "messages"}`
    : "Messages"
);
</script>
<style scoped lang="scss">
.day-label {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--phoenix-body-bg);
  border-bottom: 1px solid var(--phoenix-border-color);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.message-row {
  display: grid;
  grid-template-columns: 4.5rem minmax(0, 1fr);
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.55rem 1rem;
  border: 0;
  border-bottom: 1px solid var(--phoenix-border-color);
  background: transparent;
  color: inherit;

  &:hover {
    background: var(--phoenix-secondary-bg);
  }
  &.selected {
    background: var(--phoenix-primary-bg-subtle);
    box-shadow: inset 3px 0 0 var(--phoenix-primary);
  }
}
.clock {
  font-variant-numeric: tabular-nums;
  padding-top: 0.1rem;
}
.min-w-0 {
  min-width: 0;
}
.body {
  display: block;
  margin-top: 0.15rem;
  font-family: var(--phoenix-font-monospace);
  font-size: 0.85rem;
}
</style>
